import { useEffect, useMemo } from 'react'
import type { Asset } from '../types'
import { usePaintSelection } from '../hooks/usePaintSelection'
import { useContainerWidth, useJustifiedRows } from '../hooks/useJustifiedRows'
import { AssetCard } from './AssetCard'
import { finishLater } from '../lookalike/finishExternal'

/** Roughly how tall a row wants to be before it is adjusted to fit. */
const TARGET_HEIGHT = 180
const GAP = 14
const GAP_COMPACT = 6

/**
 * In a narrow container the row height is worked out from the width instead of
 * being fixed, so that a row holds about this many images.
 *
 * That number is the whole point. Justifying a row means adjusting its height
 * until it ends flush, and how violent that adjustment is depends entirely on
 * how many images share it: with two, filling the width can demand half again
 * the height and the page lurches as you scroll; with six, the same correction
 * is spread six ways and nobody sees it. Making the pictures smaller is what
 * buys the tidy edge.
 */
const COMPACT_PER_ROW = 4
/** A mildly portrait pack shot, which is the common case. */
const TYPICAL_RATIO = 0.8
/**
 * Small enough to fit, big enough to recognise a bottle.
 *
 * Four per row is about the edge of where justification still looks calm.
 * Fewer than that and filling the width starts demanding large changes in row
 * height, because there are too few images to spread the correction across —
 * which is the whole reason this number exists rather than a fixed size.
 */
const COMPACT_MIN_HEIGHT = 74
const COMPACT_MAX_HEIGHT = 190

interface AssetGridProps {
  assets: Asset[]
  selectedIds: string[]
  onToggle: (asset: Asset) => void
  loading?: boolean
  compact?: boolean
  actionLabel?: string
  onAction?: (asset: Asset) => void
  emptyTitle?: string
  emptyHint?: string
  /**
   * Enables press-and-sweep selection. Given both a select and a deselect
   * rather than a toggle, because a sweep has one verb for its whole length.
   */
  onPaint?: (asset: Asset, select: boolean) => void
  /** An extra class on every card; see AssetCard's `dragClass`. */
  dragClass?: string
  /** The sweep starts only with Shift held. */
  paintNeedsShift?: boolean
  /** Held for three seconds on a card. See AssetCard. */
  onLongPress?: (asset: Asset) => void
  /** The View larger button on each card. See AssetCard. */
  onView?: (asset: Asset) => void
  /** View details on each card (Amazon's products). See AssetCard. */
  onDetails?: (asset: Asset) => void
}

export function AssetGrid({
  assets,
  selectedIds,
  onToggle,
  loading = false,
  compact = false,
  actionLabel,
  onAction,
  emptyTitle = 'No images match these filters',
  emptyHint = 'Try removing a tag or searching for something broader.',
  onPaint,
  dragClass,
  paintNeedsShift = false,
  onLongPress,
  onView,
  onDetails,
}: AssetGridProps) {
  const paint = usePaintSelection(onPaint, paintNeedsShift)
  // Amazon and Google pictures in a library, still without their look and a
  // small copy: this browser makes them (lookalike/finishExternal.ts).
  useEffect(() => finishLater(assets), [assets])
  const { ref, width } = useContainerWidth<HTMLDivElement>()

  /**
   * Equal cells are the fallback if justified rows ever stop working in a
   * narrow panel: a plain grid, flush by construction, never moves, at the cost
   * of empty space around images whose shape does not match the cell. Switching
   * is this one word.
   */
  const cells = false
  const gap = compact ? GAP_COMPACT : GAP

  // Narrow: aim for a set number of images per row and let the height follow.
  // Wide: a fixed height, because the width is already generous enough for the
  // rows to come out long.
  const target = compact
    ? Math.min(
        COMPACT_MAX_HEIGHT,
        Math.max(
          COMPACT_MIN_HEIGHT,
          (width - (COMPACT_PER_ROW - 1) * gap) / (COMPACT_PER_ROW * TYPICAL_RATIO),
        ),
      )
    : TARGET_HEIGHT

  const rows = useJustifiedRows(cells ? [] : assets, width, target, gap)
  // A Set, because `includes` on an array of a few hundred ids runs once per
  // card on every render.
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  if (loading && assets.length === 0) {
    return (
      <div ref={ref} className={`ww-grid${cells ? ' ww-grid--cells' : ''}`}>
        {Array.from({ length: compact ? 6 : 12 }).map((_, i) => (
          <div
            key={i}
            className="ww-skeleton"
            style={{ aspectRatio: '4 / 5', height: 'var(--ww-row-height)' }}
          />
        ))}
      </div>
    )
  }

  if (assets.length === 0) {
    return (
      <div className="ww-empty">
        <span className="ww-empty__title">{emptyTitle}</span>
        <span>{emptyHint}</span>
      </div>
    )
  }

  return (
    <div
      ref={ref}
      className={`ww-grid${cells ? ' ww-grid--cells' : ''}${
        paint.active ? ' ww-grid--painting' : ''
      }`}
    >
      {cells
        ? assets.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              cell
              selected={selectedSet.has(asset.id)}
              onToggle={onToggle}
              actionLabel={actionLabel}
              onAction={onAction}
              onPointerDown={paint.onPointerDown}
              onPointerEnter={paint.onPointerEnter}
              shouldSwallowClick={paint.shouldSwallowClick}
              dragClass={dragClass}
              onLongPress={onLongPress}
              onView={onView}
              onDetails={onDetails}
            />
          ))
        : rows.map((row) => (
        <div className="ww-row" key={row[0].asset.id}>
          {row.map(({ asset, width: w, height: h }) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              selected={selectedSet.has(asset.id)}
              onToggle={onToggle}
              actionLabel={actionLabel}
              onAction={onAction}
              onPointerDown={paint.onPointerDown}
              onPointerEnter={paint.onPointerEnter}
              shouldSwallowClick={paint.shouldSwallowClick}
              dragClass={dragClass}
              onLongPress={onLongPress}
              onView={onView}
              onDetails={onDetails}
              width={w}
              height={h}
            />
              ))}
            </div>
          ))}
    </div>
  )
}
